import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Checkbox,
  Box,
  Typography,
  Alert,
  MenuItem, 
  Tooltip,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import { useMsal } from '@azure/msal-react';
import { UserContext } from '../../contexts/UserContext';
import { getApiBaseUrl, getAuthConfig } from '../../services/configService';
import { buildJournalFormData } from '../../utils/buildJournalFormData';

import './BulkJournalModal.css';

/**
 * BulkJournalModal
 *
 * Props:
 * - open
 * - onClose
 * - visit
 * - candidates: Array<{ journalType, source }>
 * - refreshVisitDetails
 */
const BulkJournalModal = ({
  open,
  onClose,
  visit,
  candidates = [],
  refreshVisitDetails,
}) => {
  const { user } = useContext(UserContext);
  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();

  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  /* --------------------------
     Auth
  -------------------------- */
  const getApiAccessToken = async () => {
    if (!accounts.length) return null;

    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };

  /* --------------------------
     Build rows on open
  -------------------------- */
  useEffect(() => {
    if (!open) return;

    const builtRows = candidates.map((c, index) => ({
      id: `${c.journalType}-${index}`,
      include: false, // ✅ default unchecked
      built: buildJournalFormData({
        journalType: c.journalType,
        visit,
        invoiceLine: c.journalType === 'invoice' ? c.source : undefined,
        chargeCaptureItem:
          c.journalType === 'charge_capture' ||
          c.journalType === 'medical_note'
            ? c.source
            : undefined,
        discountItem:
          c.journalType === 'discount' ? c.source : undefined,
        currentUser: user,
      }),
    }));

    setRows(builtRows);
    setError(null);
  }, [open, candidates, visit, user]);

  /* --------------------------
     Helpers
  -------------------------- */
  const updateRowField = (rowId, field, value) => {
    setRows(prev =>
      prev.map(r =>
        r.id === rowId
          ? {
              ...r,
              built: {
                ...r.built,
                formData: {
                  ...r.built.formData,
                  [field]: value,
                },
              },
            }
          : r
      )
    );
  };

  const toggleInclude = rowId => {
    setRows(prev =>
      prev.map(r =>
        r.id === rowId ? { ...r, include: !r.include } : r
      )
    );
  };

  const toggleSelectAll = () => {
    setRows(prev => {
      const shouldSelectAll = !prev.every(r => r.include);
      return prev.map(r => ({ ...r, include: shouldSelectAll }));
    });
  };  

  /* --------------------------
     Submit bulk
  -------------------------- */
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const token = await getApiAccessToken();
      if (!token) throw new Error('Unable to acquire API access token');

      const payload = rows
        .filter(r => r.include)
        .map(r => ({
          server_id: '',
          ...r.built.formData,
          email:
            user?.mail ||
            user?.email ||
            user?.userPrincipalName ||
            '',
        }));

      if (!payload.length) {
        throw new Error('No rows selected for submission');
      }

      const res = await fetch(`${getApiBaseUrl()}/journal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      await refreshVisitDetails?.({ silent: true });
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const includedCount = rows.filter(r => r.include).length;
  const allSelected = rows.length > 0 && includedCount === rows.length;
  const someSelected = includedCount > 0 && includedCount < rows.length;

  /* --------------------------
     Render
  -------------------------- */
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      className="bulk-journal-dialog"
    >
      {/* ✅ Sticky Header */}
      <Box className="bulk-journal-header">
        <span className="bulk-journal-title">Bulk Journal</span>

        <Button
          variant="contained"
          disabled={submitting || includedCount === 0}
          onClick={handleSubmit}
          startIcon={
            submitting ? (
              <CircularProgress
                size={16}
                thickness={5}
                sx={{ color: '#fff' }}
              />
            ) : null
          }
        >
          {submitting
            ? 'Submitting…'
            : `Submit Selected (${includedCount})`}
        </Button>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {error && (
          <Box sx={{ px: 2, py: 1 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* ✅ Column Header */}
        <Box
          className="bulk-journal-columns"
          sx={{
            display: 'grid',
            gridTemplateColumns:
              '120px 160px 120px 140px 120px 110px 110px 160px 1fr 80px',
            gap: 1,
            px: 2,
            py: 1,
          }}
        >
          <div>Type</div>
          <div>Label</div>
          <div>PIMS ID</div>
          <div>Completed</div>
          <div>Staff</div>
          <div>Price</div>
          <div>Recovered</div>
          <div>Reason</div>
          <div>Note</div>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >        
            <Tooltip title={allSelected ? 'Clear all' : 'Select all'}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={toggleSelectAll}
              />
            </Tooltip>
          </Box>
        </Box>

        {/* ✅ Rows */}
        {rows.map(row => {
          const { built, include, id } = row;
          const { formData, displayedFields, readonlyFields } = built;

          const show = field => displayedFields.has(field);
          const readOnly = field => readonlyFields.has(field);

          return (
            <Box
              key={id}
              className={`bulk-journal-row ${include ? 'included' : ''}`}
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  '120px 160px 120px 140px 120px 110px 110px 160px 1fr 80px',
                gap: 1,
                alignItems: 'center',
                px: 2,
                py: 0.75,
              }}
            >
              <div style={{ fontWeight: 500 }}>
                {formData.charge_type || built.journalType}
              </div>

              <TextField
                size="small"
                value={formData.label || ''}
                disabled={!show('label') || readOnly('label')}
                onChange={e =>
                  updateRowField(id, 'label', e.target.value)
                }
              />

              <TextField
                size="small"
                value={formData.invoiceitemid || ''}
                disabled={readOnly('invoiceitemid')}
                onChange={e =>
                  updateRowField(id, 'invoiceitemid', e.target.value)
                }
              />

              <TextField
                size="small"
                value={formData.completed_date || ''}
                disabled
              />

              <TextField
                size="small"
                value={formData.staff_id || ''}
                disabled={readOnly('staff_id')}
                onChange={e =>
                  updateRowField(id, 'staff_id', e.target.value)
                }
              />

              <TextField
                size="small"
                type="number"
                value={formData.expected_charge ?? ''}
                disabled={readOnly('expected_charge')}
                onChange={e =>
                  updateRowField(id, 'expected_charge', e.target.value)
                }
              />

              <TextField
                size="small"
                type="number"
                value={formData.adjusted_charge ?? ''}
                disabled={readOnly('adjusted_charge')}
                onChange={e =>
                  updateRowField(id, 'adjusted_charge', e.target.value)
                }
              />

              {show('reason') ? (
                <TextField
                  select
                  size="small"
                  value={formData.reason || ''}
                  disabled={readOnly('reason')}
                  onChange={e =>
                    updateRowField(id, 'reason', e.target.value)
                  }
                >
                  {[
                    'Caught in Realtime',
                    'Changed by Doctor',
                    "Code Doesn't Exist",
                    'Estimate Issue',
                    'Hospital Nuance',
                    'Overcharge',
                    'PVL Mismatch',
                    'Workflow Error',
                    'Other',
                  ].map(r => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <div />
              )}

              <TextField
                size="small"
                value={formData.note || ''}
                disabled={readOnly('note')}
                onChange={e =>
                  updateRowField(id, 'note', e.target.value)
                }
              />

              <Checkbox
                checked={include}
                onChange={() => toggleInclude(id)}
              />
            </Box>
          );
        })}
      </DialogContent>

      {/* ✅ Footer */}
      <Box className="bulk-journal-actions">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
};

export default BulkJournalModal;
